// ./src/components/generic/LoadingOverlay/LoadingOverlay.tsx
import React from "react";
import { Box, Center, Stack, Text } from "@mantine/core";

import classes from "./LoadingOverlay.module.css";

import { Logo } from "../Logo";

// ============================================
// Types
// ============================================

export interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  visible: boolean;
  /** Optional text to display below the loading indicator */
  loadingText?: string;
  /** Z-index for the overlay */
  zIndex?: number;
  /** Show app version */
  showVersion?: boolean;
  /** Show logo */
  showLogo?: boolean;
}

// ============================================
// Main Component
// ============================================

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  loadingText,
  zIndex = 200,
  showVersion = true,
  showLogo = true,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <Box className={classes.overlay} style={{ zIndex }}>
      {/* Subtle background glow for depth */}
      <div className={classes.backgroundGlow} />

      {/* === Main Content === */}
      <Center h="100%" pos="relative" style={{ zIndex: 10 }}>
        <Stack align="center" gap={0} className={classes.contentWrapper}>
          {/* Logo with progress ring */}
          {showLogo && (
            <div className={classes.logoContainer}>
              {/* Progress ring background */}
              <div className={classes.progressRing} />

              {/* Animated arc - primary */}
              <div className={classes.progressArc} />

              {/* Animated arc - secondary (counter-rotating) */}
              <div className={classes.progressArcSecondary} />

              {/* Subtle glow behind logo */}
              <div className={classes.logoGlow} />

              {/* Circular backdrop to match progress ring */}
              <div className={classes.logoCircle} />

              {/* Logo */}
              <div className={classes.logoImage}>
                <Logo size="xlarge" />
              </div>
            </div>
          )}

          {/* Text section */}
          <div className={classes.textSection}>
            {loadingText && (
              <Text className={classes.loadingText}>
                {loadingText}
              </Text>
            )}

            {showVersion && (
              <Text className={classes.versionText}>
                v{import.meta.env.VITE_APP_VERSION}
              </Text>
            )}
          </div>
        </Stack>
      </Center>
    </Box>
  );
};